/**
 * Number / currency / duration formatters for the V2 dashboard.
 *
 * All accept a `null | undefined` and return the em-dash placeholder so
 * callers don't have to null-guard every render. Locale-aware via Intl.
 *
 * Currency defaults to EUR — when a per-company currency setting lands in
 * the registry, callers will pass the company's currency code instead.
 */

const DASH = '—';
const DEFAULT_CURRENCY = 'EUR';

export function formatNumber(value: number | null | undefined, locale: string = 'en'): string {
    if (value == null || isNaN(value)) return DASH;
    return new Intl.NumberFormat(locale).format(Math.round(value));
}

export function formatCurrency(
    value: number | null | undefined,
    locale: string = 'en',
    currency: string = DEFAULT_CURRENCY,
): string {
    if (value == null || isNaN(value)) return DASH;
    const abs = Math.abs(value);
    // Compact notation kicks in for big numbers so "€1,234,500" doesn't
    // blow up the hero card. Symbol stays attached via Intl.
    if (abs >= 100_000) {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            notation: 'compact',
            maximumFractionDigits: 1,
        }).format(value);
    }
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
    }).format(value);
}

export function formatPercent(value: number | null | undefined, decimals: number = 1): string {
    if (value == null || isNaN(value)) return DASH;
    return `${value.toFixed(decimals)}%`;
}

export function formatDuration(seconds: number | null | undefined): string {
    if (seconds == null || isNaN(seconds) || seconds === 0) return DASH;
    const totalMinutes = Math.round(seconds / 60);
    if (totalMinutes < 60) return `${totalMinutes}m`;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours < 24) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
}

export function formatMonth(monthStr: string, locale: string = 'en'): string {
    // Backend ships "YYYY-MM-01" — pin the date so timezone never flips the month.
    const d = new Date(`${monthStr}T00:00:00`);
    if (isNaN(d.getTime())) return monthStr;
    return new Intl.DateTimeFormat(locale, { month: 'short' }).format(d);
}
